import axiosInstance from './Instance/axiosInstance';
import {
    ArchiveListResult,
    ArchiveQuery,
    ArchiveRecord,
    ArchiveSummaryResult,
    emptyArchivePagination,
    RestoreManyResult,
} from '../types/archive';

/**
 * The API accepts comma separated lists for `collectionName` / `status`, so
 * arrays are joined here and empty filters are dropped entirely.
 */
const toParams = (query: ArchiveQuery = {}): Record<string, string | number | boolean> => {
    const params: Record<string, string | number | boolean> = {};

    if (query.collectionName?.length) params.collectionName = query.collectionName.join(',');
    if (query.status?.length) params.status = query.status.join(',');
    if (query.documentId?.trim()) params.documentId = query.documentId.trim();
    if (query.userId?.trim()) params.userId = query.userId.trim();
    if (query.search?.trim()) params.search = query.search.trim();
    if (query.from) params.from = query.from;
    if (query.to) params.to = query.to;
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.sort) params.sort = query.sort;
    if (query.includeData) params.includeData = true;

    return params;
};

export const getArchives = async (query: ArchiveQuery = {}): Promise<ArchiveListResult> => {
    const res = await axiosInstance.get('/archive', { params: toParams(query) });
    return {
        data: res.data?.data ?? [],
        pagination: res.data?.pagination ?? { ...emptyArchivePagination, page: query.page ?? 1 },
    };
};

/** Counts per model — drives the model filter options. */
export const getArchiveSummary = async (query: ArchiveQuery = {}): Promise<ArchiveSummaryResult> => {
    const res = await axiosInstance.get('/archive/summary', { params: toParams(query) });
    return {
        summary: res.data?.data ?? [],
        restorableModels: res.data?.restorableModels ?? [],
    };
};

/** A single archived record with its full snapshot. */
export const getArchiveById = async (id: string): Promise<ArchiveRecord | null> => {
    const res = await axiosInstance.get(`/archive/${id}`);
    return res.data?.data ?? null;
};

/** Puts one deleted record back into its own collection. */
export const restoreArchive = async (id: string): Promise<ArchiveRecord | null> => {
    const res = await axiosInstance.post(`/archive/${id}/restore`);
    return res.data?.data ?? null;
};

/** Puts several deleted records back in one call. */
export const restoreArchives = async (ids: string[]): Promise<RestoreManyResult> => {
    const res = await axiosInstance.post('/archive/restore', { ids });
    return {
        results: res.data?.data ?? [],
        restored: res.data?.restored ?? 0,
        failed: res.data?.failed ?? 0,
    };
};

/** Drops the snapshot for good — the record can no longer be restored. */
export const purgeArchive = async (id: string): Promise<void> => {
    await axiosInstance.delete(`/archive/${id}`);
};
